/**
 * TanStack Query hooks — scoring de comparaison (chunk 9.AJ.1).
 *
 *   GET /v1/projects/:id/scoring          classement + explication du calcul
 *   GET /v1/comparison-criteria-weights   réglages effectifs du cabinet
 *   PUT /v1/comparison-criteria-weights   remplace un lot de réglages
 *
 * L'interface ne recalcule RIEN. Le classement, les contributions et les critères
 * écartés arrivent déjà décidés par `scoreComparison()` côté serveur. Refaire une
 * somme pondérée dans un composant React permettrait à l'écran d'afficher un score
 * que le serveur ne reconnaîtrait pas.
 */

import type { Comparison, CriterionWeightRow } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** La charge de `GET /v1/projects/:id/scoring`. */
export type ScoringPayload = Comparison.ScoringResult & {
  /** Les réglages utilisés, avec leur provenance — cabinet, global, ou registre. */
  settings: CriterionWeightRow[];
};

export function useProjectScoring(projectId: string | undefined) {
  return useQuery({
    queryKey: ["scoring", projectId],
    queryFn: () => api.get<ScoringPayload>(`/v1/projects/${projectId}/scoring`),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

export function useCriterionWeights() {
  return useQuery({
    queryKey: ["criterion-weights"],
    queryFn: () => api.get<CriterionWeightRow[]>("/v1/comparison-criteria-weights"),
  });
}

export type WeightSetting = { field_key: string; weight: number; enabled: boolean };

export function useUpdateCriterionWeights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: WeightSetting[]) =>
      api.patch<CriterionWeightRow[]>("/v1/comparison-criteria-weights", { settings }),
    onSuccess: () => {
      // Un poids change TOUS les classements du cabinet, pas seulement celui qu'on
      // regarde : on invalide les deux clés plutôt que de rafraîchir la page ouverte.
      void qc.invalidateQueries({ queryKey: ["criterion-weights"] });
      void qc.invalidateQueries({ queryKey: ["scoring"] });
    },
  });
}
